import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, X } from 'lucide-react';
import { useCreateRequirement, useUpdateRequirement, useRequirements, useComplianceFrameworks, useENSMeasures } from '@/hooks/useRequirements';
import { useOperators } from '@/hooks/usePlotterMaintenance';
import type {
  Requirement,
  RequirementFormData,
  RequirementCategory,
  RequirementLevel,
  RequirementPriority,
  SourceType,
  VerificationMethod,
} from '@/types/requirements';
import {
  categoryLabels,
  levelLabels,
  priorityLabels,
  sourceTypeLabels,
  verificationMethodLabels,
} from '@/types/requirements';

interface RequirementFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requirement?: Requirement | null;
  parentRequirementId?: string;
  onSuccess?: () => void;
}

const defaultFormData: RequirementFormData = {
  category: 'functional',
  level: 'srs',
  title: '',
  description: '',
  source_type: 'internal',
  priority: 'medium',
  compliance_standards: [],
  ens_measures: [],
};

export function RequirementForm({
  open,
  onOpenChange,
  requirement,
  parentRequirementId,
  onSuccess,
}: RequirementFormProps) {
  const [formData, setFormData] = useState<RequirementFormData>(defaultFormData);
  const [activeTab, setActiveTab] = useState('basic');

  const createRequirement = useCreateRequirement();
  const updateRequirement = useUpdateRequirement();
  const { data: requirements } = useRequirements();
  const { data: frameworks } = useComplianceFrameworks();
  const { data: ensMeasures } = useENSMeasures();
  const { data: operators } = useOperators();

  const isEdit = !!requirement;
  const isLoading = createRequirement.isPending || updateRequirement.isPending;

  useEffect(() => {
    if (open) {
      if (requirement) {
        setFormData({
          category: requirement.category,
          subcategory: requirement.subcategory,
          level: requirement.level,
          title: requirement.title,
          description: requirement.description,
          rationale: requirement.rationale,
          acceptance_criteria: requirement.acceptance_criteria,
          source_type: requirement.source_type,
          source_reference: requirement.source_reference,
          parent_requirement_id: requirement.parent_requirement_id,
          priority: requirement.priority,
          verification_method: requirement.verification_method,
          assigned_to: requirement.assigned_to,
          responsible_team: requirement.responsible_team,
          compliance_standards: requirement.compliance_standards || [],
          ens_measures: requirement.ens_measures || [],
          wbs_reference: requirement.wbs_reference,
          estimated_effort_hours: requirement.estimated_effort_hours,
          target_release: requirement.target_release,
        });
      } else {
        setFormData({
          ...defaultFormData,
          parent_requirement_id: parentRequirementId,
        });
      }
      setActiveTab('basic');
    }
  }, [open, requirement, parentRequirementId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    try {
      if (isEdit && requirement) {
        await updateRequirement.mutateAsync({
          id: requirement.id,
          updates: {
            ...formData,
            version: requirement.version,
          } as Partial<Requirement>,
        });
      } else {
        await createRequirement.mutateAsync(formData);
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const toggleStandard = (code: string) => {
    setFormData((prev) => ({
      ...prev,
      compliance_standards: prev.compliance_standards.includes(code)
        ? prev.compliance_standards.filter((s) => s !== code)
        : [...prev.compliance_standards, code],
    }));
  };

  const toggleENSMeasure = (code: string) => {
    setFormData((prev) => ({
      ...prev,
      ens_measures: prev.ens_measures.includes(code)
        ? prev.ens_measures.filter((m) => m !== code)
        : [...prev.ens_measures, code],
    }));
  };

  const parentOptions = requirements?.filter(
    (r) => r.id !== requirement?.id && r.level !== 'tst'
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Requisito' : 'Nuevo Requisito'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Editando ${requirement?.requirement_id}`
              : 'Complete la informacion del requisito segun ISO 29148 / PECAL 2210'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basico</TabsTrigger>
              <TabsTrigger value="traceability">Trazabilidad</TabsTrigger>
              <TabsTrigger value="compliance">Compliance</TabsTrigger>
              <TabsTrigger value="planning">Planificacion</TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[400px] mt-4 pr-4">
              {/* Tab: Informacion Basica */}
              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) =>
                        setFormData((prev) => ({ ...prev, category: v as RequirementCategory }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categoryLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Nivel *</Label>
                    <Select
                      value={formData.level}
                      onValueChange={(v) =>
                        setFormData((prev) => ({ ...prev, level: v as RequirementLevel }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(levelLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Prioridad *</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) =>
                        setFormData((prev) => ({ ...prev, priority: v as RequirementPriority }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(priorityLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Titulo *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Titulo claro y conciso del requisito"
                    maxLength={500}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Descripcion *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Descripcion detallada del requisito..."
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Justificacion (Rationale)</Label>
                  <Textarea
                    value={formData.rationale || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, rationale: e.target.value }))
                    }
                    placeholder="Por que es necesario este requisito..."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Criterios de Aceptacion</Label>
                  <Textarea
                    value={formData.acceptance_criteria || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, acceptance_criteria: e.target.value }))
                    }
                    placeholder="Condiciones para considerar el requisito cumplido..."
                    rows={2}
                  />
                </div>
              </TabsContent>

              {/* Tab: Trazabilidad */}
              <TabsContent value="traceability" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Origen *</Label>
                    <Select
                      value={formData.source_type}
                      onValueChange={(v) =>
                        setFormData((prev) => ({ ...prev, source_type: v as SourceType }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(sourceTypeLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Referencia de Origen</Label>
                    <Input
                      value={formData.source_reference || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, source_reference: e.target.value }))
                      }
                      placeholder="Ej: Contrato ABC-2026 Clausula 4.2"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Requisito Padre (Trazabilidad Vertical)</Label>
                  <Select
                    value={formData.parent_requirement_id || 'none'}
                    onValueChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        parent_requirement_id: v === 'none' ? undefined : v,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin requisito padre" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin requisito padre</SelectItem>
                      {parentOptions?.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.requirement_id} - {r.title.substring(0, 50)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    PECAL 2210: Trazabilidad bidireccional obligatoria
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Metodo de Verificacion</Label>
                  <Select
                    value={formData.verification_method || 'none'}
                    onValueChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        verification_method: v === 'none' ? undefined : (v as VerificationMethod),
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar metodo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No especificado</SelectItem>
                      {Object.entries(verificationMethodLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              {/* Tab: Compliance */}
              <TabsContent value="compliance" className="space-y-4">
                <div className="space-y-2">
                  <Label>Marcos Normativos Aplicables</Label>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                    {frameworks?.map((f) => (
                      <Badge
                        key={f.code}
                        variant={
                          formData.compliance_standards.includes(f.code)
                            ? 'default'
                            : 'outline'
                        }
                        className="cursor-pointer"
                        onClick={() => toggleStandard(f.code)}
                      >
                        {formData.compliance_standards.includes(f.code) && (
                          <X className="h-3 w-3 mr-1" />
                        )}
                        {f.code}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Seleccione los estandares que aplican a este requisito
                  </p>
                </div>

                {formData.compliance_standards.includes('ENS') && (
                  <div className="space-y-2">
                    <Label>Medidas ENS Relacionadas</Label>
                    <ScrollArea className="h-[200px] border rounded-lg p-2">
                      <div className="space-y-2">
                        {ensMeasures?.map((m) => (
                          <div
                            key={m.measure_code}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={m.measure_code}
                              checked={formData.ens_measures.includes(m.measure_code)}
                              onCheckedChange={() => toggleENSMeasure(m.measure_code)}
                            />
                            <label
                              htmlFor={m.measure_code}
                              className="text-sm cursor-pointer"
                            >
                              <span className="font-mono text-xs">{m.measure_code}</span>{' '}
                              - {m.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </TabsContent>

              {/* Tab: Planificacion */}
              <TabsContent value="planning" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Asignado a</Label>
                    <Select
                      value={formData.assigned_to || 'none'}
                      onValueChange={(v) =>
                        setFormData((prev) => ({
                          ...prev,
                          assigned_to: v === 'none' ? undefined : v,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sin asignar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {operators?.map((op) => (
                          <SelectItem key={op.id} value={op.id}>
                            {op.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Equipo Responsable</Label>
                    <Input
                      value={formData.responsible_team || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, responsible_team: e.target.value }))
                      }
                      placeholder="Ej: Desarrollo Backend"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Referencia WBS</Label>
                    <Input
                      value={formData.wbs_reference || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, wbs_reference: e.target.value }))
                      }
                      placeholder="Ej: 1.2.3.4"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Esfuerzo Estimado (h)</Label>
                    <Input
                      type="number"
                      value={formData.estimated_effort_hours || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          estimated_effort_hours: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        }))
                      }
                      placeholder="Horas"
                      min={0}
                      step={0.5}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Release Objetivo</Label>
                    <Input
                      value={formData.target_release || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, target_release: e.target.value }))
                      }
                      placeholder="Ej: v2.14.0"
                    />
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading || !formData.title || !formData.description}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEdit ? 'Guardando...' : 'Creando...'}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  {isEdit ? 'Guardar Cambios' : 'Crear Requisito'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
